import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { getWeekDates, formatWeekLabel } from '@/utils/planning';
import { EasykitRefreshButton } from './EasykitRefreshButton';
import { EasykitTaskSearch } from './EasykitTaskSearch';

type CreateType = 'feestdag' | 'verlof' | 'vrije_taak';

const typeLabels: Record<CreateType, string> = {
  feestdag: 'Feestdag',
  verlof: 'Verlof',
  vrije_taak: 'Vrije taak',
};

function OverlayToggle() {
  const showOverlayTasks = useEasykitPlanningStore((s) => s.showOverlayTasks);
  const setShowOverlayTasks = useEasykitPlanningStore((s) => s.setShowOverlayTasks);
  return (
    <button
      type="button"
      onClick={() => setShowOverlayTasks(!showOverlayTasks)}
      aria-pressed={showOverlayTasks}
      title="Toon andere taken van EK chauffeurs (alleen-lezen)"
      className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${
        showOverlayTasks
          ? 'border-slate-400 bg-slate-100 text-slate-700'
          : 'border-gray-300 text-td-navy hover:bg-gray-50'
      }`}
    >
      Andere taken
    </button>
  );
}

export function EasykitPlanningHeader() {
  const { weekOffset, lastRefreshed, tasks, loading, nextWeek, prevWeek, goToCurrentWeek, fetchPlanning, enterCreateMode } =
    useEasykitPlanningStore(useShallow((s) => ({
      weekOffset: s.weekOffset,
      lastRefreshed: s.lastRefreshed,
      tasks: s.tasks,
      loading: s.loading,
      nextWeek: s.nextWeek,
      prevWeek: s.prevWeek,
      goToCurrentWeek: s.goToCurrentWeek,
      fetchPlanning: s.fetchPlanning,
      enterCreateMode: s.enterCreateMode,
    })));

  const [vrijeTaakInput, setVrijeTaakInput] = useState('');
  const [showVrijeTaakInput, setShowVrijeTaakInput] = useState(false);

  const { days: allDays, weekNumber } = getWeekDates(weekOffset);
  // EasyKit only uses Mon-Fri (5 days), drop Saturday for label
  const days = allDays.slice(0, 5);
  const weekLabel = formatWeekLabel(weekNumber, days);

  const refreshTime = lastRefreshed
    ? lastRefreshed.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })
    : null;

  function handleButtonClick(type: CreateType) {
    if (type === 'vrije_taak') {
      setShowVrijeTaakInput(true);
      setVrijeTaakInput('');
    } else {
      enterCreateMode(type);
    }
  }

  function submitVrijeTaak() {
    if (!vrijeTaakInput.trim()) return;
    enterCreateMode('vrije_taak', vrijeTaakInput.trim());
    setShowVrijeTaakInput(false);
    setVrijeTaakInput('');
  }

  return (
    <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-3">
      {/* Top row: create buttons + search + refresh */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Left: create buttons */}
        <div className="hidden lg:flex items-center gap-1.5">
          {(['feestdag', 'verlof', 'vrije_taak'] as CreateType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleButtonClick(type)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue-dark transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {typeLabels[type]}
            </button>
          ))}
          {showVrijeTaakInput && (
            <div className="flex items-center gap-1.5 ml-1">
              <label htmlFor="ek-vrije-taak-input" className="sr-only">Taaknaam</label>
              <input
                id="ek-vrije-taak-input"
                type="text"
                value={vrijeTaakInput}
                onChange={(e) => setVrijeTaakInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitVrijeTaak(); }}
                placeholder="Taaknaam..."
                className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs w-40"
                autoFocus
              />
              <button
                type="button"
                onClick={submitVrijeTaak}
                disabled={!vrijeTaakInput.trim()}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue-dark transition-colors disabled:opacity-50"
              >
                OK
              </button>
              <button
                type="button"
                onClick={() => setShowVrijeTaakInput(false)}
                className="px-2 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                Annuleren
              </button>
            </div>
          )}
        </div>

        {/* Search — debounced, opens schedule mode or navigates to a planned task. */}
        <EasykitTaskSearch />

        {/* Right: overlay toggle + refresh */}
        <div className="hidden lg:flex items-center gap-2">
          <OverlayToggle />
          <EasykitRefreshButton onClick={() => fetchPlanning()} loading={loading} />
        </div>
      </div>

      {/* Week nav — own row, centered */}
      <div className="flex items-center justify-center gap-1 mt-2">
        <button
          type="button"
          onClick={goToCurrentWeek}
          disabled={weekOffset === 0}
          className="px-2.5 py-1 text-xs font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Vandaag
        </button>
        <button
          type="button"
          onClick={prevWeek}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Vorige week"
        >
          <svg className="w-4 h-4 text-td-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm lg:text-base font-bold text-td-navy whitespace-nowrap">
          {weekLabel}
        </span>
        <button
          type="button"
          onClick={nextWeek}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Volgende week"
        >
          <svg className="w-4 h-4 text-td-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Mobile create + refresh row */}
      <div className="flex items-center gap-2 mt-3 lg:hidden overflow-x-auto pb-1">
        {(['feestdag', 'verlof', 'vrije_taak'] as CreateType[]).map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => handleButtonClick(type)}
            className="px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap border border-td-blue text-td-blue hover:bg-td-blue/5 transition-colors"
          >
            + {typeLabels[type]}
          </button>
        ))}
        <EasykitRefreshButton onClick={() => fetchPlanning()} loading={loading} className="ml-auto shrink-0" />
      </div>

      {/* Mobile vrije taak input */}
      {showVrijeTaakInput && (
        <div className="flex items-center gap-2 mt-2 lg:hidden">
          <label htmlFor="ek-vrije-taak-input-mobile" className="sr-only">Taaknaam</label>
          <input
            id="ek-vrije-taak-input-mobile"
            type="text"
            value={vrijeTaakInput}
            onChange={(e) => setVrijeTaakInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submitVrijeTaak(); }}
            placeholder="Taaknaam..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-xs"
            autoFocus
          />
          <button
            type="button"
            onClick={submitVrijeTaak}
            disabled={!vrijeTaakInput.trim()}
            className="px-3 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue-dark transition-colors disabled:opacity-50"
          >
            OK
          </button>
        </div>
      )}

      {/* Task count + refresh time */}
      <p className="text-xs text-td-muted mt-2">
        {tasks.length} taken gevonden
        {refreshTime && <span> · vernieuwd om {refreshTime}</span>}
      </p>
    </div>
  );
}
